import { EventArgs } from 'react-ga';

import { AnalyticsEventKey } from 'app/services/analytics/events';

// Local Typings
export type EventSetter = (gaEventArgOverrides?: Partial<EventArgs>) => EventArgs;

// Local Variables
const categories = {
  claimFlowQuickPay: 'Claim Flow QuickPay',
  factorLandingPage: 'Factor Landing Page',
  miscellaneous: 'Miscellaneous',
  newClaimFlowQuickPay: 'New Claim Flow QuickPay',
  noa: 'NOA',
  payeeDashboardQuickPay: 'Payee Dashboard QuickPay',
  payeeDashboardSelectCarrier: 'Payee Dashboard Select Carrier',
  payeeInvoiceShowQuickPayAvailable: 'Payee Invoice Show QuickPay Available',
  payeeInvoicesIndexBulkQuickPay: 'Payee Invoices Index Bulk QuickPay',
  payeeInvoicesIndexQuickPayAvailable: 'Payee Invoices Index QuickPay Available',
  payeeSelectCarrierLandingPage: 'Payee Select Carrier Landing Page',
  payeeSelectCarrierNavbarLink: 'Payee Select Carrier Navbar Link',
  relationshipShowPageQuickPay: 'Relationship Show Page QuickPay',
  relationshipsIndexPageQuickPay: 'Relationships Index Page QuickPay',
  submitPaperworkSuccessQuickPayDialog: 'Submit Paperwork Success QuickPay Dialog',
};

const events: Record<AnalyticsEventKey, EventSetter> = {
  claimFlowEnableBulkQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled bulk QuickPay from the relationship claim flow',
    category: categories.claimFlowQuickPay,
    ...gaEventArgOverrides,
  }),
  claimFlowEnableBulkQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the bulk enable QuickPay dialog in the claim flow',
    category: categories.claimFlowQuickPay,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  claimFlowEnableQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay from the relationship claim flow',
    category: categories.claimFlowQuickPay,
    ...gaEventArgOverrides,
  }),
  claimFlowEnableQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the enable QuickPay dialog in the claim flow',
    category: categories.claimFlowQuickPay,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  dashboardEnableQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay from the dashboard',
    category: categories.payeeDashboardQuickPay,
    ...gaEventArgOverrides,
  }),
  dashboardEnableQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the enable QuickPay button on the dashboard',
    category: categories.payeeDashboardQuickPay,
    ...gaEventArgOverrides,
  }),
  dashboardEnableQuickPayVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the enable QuickPay button on the dashboard',
    category: categories.payeeDashboardQuickPay,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  dashboardSelectCarrierMoreInfoClicked: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the \'More Info\' Select Carrier button from the dashboard',
    category: categories.payeeDashboardSelectCarrier,
    ...gaEventArgOverrides,
  }),
  dashboardSelectCarrierMoreInfoVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the \'More Info\' Select Carrier button from the dashboard',
    category: categories.payeeDashboardSelectCarrier,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  dashboardSelectCarrierSelfSelectComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled Select Carrier from dashboard',
    category: categories.payeeDashboardSelectCarrier,
    ...gaEventArgOverrides,
  }),
  dashboardSelectCarrierSelfSelectPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the \'Join Now\' Select Carrier button from dashboard',
    category: categories.payeeDashboardSelectCarrier,
    ...gaEventArgOverrides,
  }),
  dashboardSelectCarrierSelfSelectVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the \'Join Now\' Select Carrier button from dashboard',
    category: categories.payeeDashboardSelectCarrier,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  dashboardSupportLinkClicked: (gaEventArgOverrides = {}) => ({
    action: 'User clicked the support link on the dashboard',
    category: categories.miscellaneous,
    ...gaEventArgOverrides,
  }),
  dashboardSupportPhoneLinkClicked: (gaEventArgOverrides = {}) => ({
    action: 'User opened the support phone dialog on the dashboard',
    category: categories.miscellaneous,
    ...gaEventArgOverrides,
  }),
  factorLandingPageAllBrokersLinkComplete: (gaEventArgOverrides = {}) => ({
    action: 'Factored carrier clicked see all brokers link on the factor landing page',
    category: categories.factorLandingPage,
    ...gaEventArgOverrides,
  }),
  factorLandingPageEmailFactorComplete: (gaEventArgOverrides = {}) => ({
    action: 'Factored carrier clicked the email my factor button on the factor landing page',
    category: categories.factorLandingPage,
    ...gaEventArgOverrides,
  }),
  factorLandingPagePayorNoaEmailComplete: (gaEventArgOverrides = {}) => ({
    action: 'Factored carrier clicked Payor NOA Email Link on the factor landing page',
    category: categories.factorLandingPage,
    ...gaEventArgOverrides,
  }),
  factorLandingPageTermsAndConditionsComplete: (gaEventArgOverrides = {}) => ({
    action: 'Factored carrier clicked Terms and Conditions Link on the factor landing page',
    category: categories.factorLandingPage,
    ...gaEventArgOverrides,
  }),
  factorLandingPageVisible: (gaEventArgOverrides = {}) => ({
    action: 'Factored carrier saw the factor landing page',
    category: categories.factorLandingPage,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  invoiceShowQuickPayAvailableInvoiceComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay on an invoice show page',
    category: categories.payeeInvoiceShowQuickPayAvailable,
    ...gaEventArgOverrides,
  }),
  invoiceShowQuickPayAvailablePrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the Enable QuickPay button on an invoice show page',
    category: categories.payeeInvoiceShowQuickPayAvailable,
    ...gaEventArgOverrides,
  }),
  invoiceShowQuickPayAvailableRelationshipComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay on a relationship from an invoice show page',
    category: categories.payeeInvoiceShowQuickPayAvailable,
    ...gaEventArgOverrides,
  }),
  invoiceShowQuickPayAvailableVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the Enable QuickPay button on an invoice show page',
    category: categories.payeeInvoiceShowQuickPayAvailable,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  invoicesIndexEnableQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay from the invoices index page',
    category: categories.payeeInvoicesIndexBulkQuickPay,
    ...gaEventArgOverrides,
  }),
  invoicesIndexEnableQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the enable QuickPay button on the invoices index page',
    category: categories.payeeInvoicesIndexBulkQuickPay,
    ...gaEventArgOverrides,
  }),
  invoicesIndexEnableQuickPayVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the enable QuickPay button on the invoices index page',
    category: categories.payeeInvoicesIndexBulkQuickPay,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  invoicesIndexQuickPayAvailableInvoiceComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay on an invoice from an invoice card on the invoices index page',
    category: categories.payeeInvoicesIndexQuickPayAvailable,
    ...gaEventArgOverrides,
  }),
  invoicesIndexQuickPayAvailablePrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the QuickPay Available button on an invoice card on the invoices index page',
    category: categories.payeeInvoicesIndexQuickPayAvailable,
    ...gaEventArgOverrides,
  }),
  invoicesIndexQuickPayAvailableRelationshipComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay on a relationship from an invoice card on the invoices index page',
    category: categories.payeeInvoicesIndexQuickPayAvailable,
    ...gaEventArgOverrides,
  }),
  invoicesIndexQuickPayAvailableVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the QuickPay Available button on an invoice card on the invoices index page',
    category: categories.payeeInvoicesIndexQuickPayAvailable,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  newClaimFlowEnableBulkQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled bulk QuickPay from the new relationship claim flow',
    category: categories.newClaimFlowQuickPay,
    ...gaEventArgOverrides,
  }),
  newClaimFlowEnableBulkQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the bulk enable QuickPay dialog in the new relationship claim flow',
    category: categories.newClaimFlowQuickPay,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  noaProcessorRedirectedToOldUI: (gaEventArgOverrides = {}) => ({
    action: 'NOA processor was redirected to the legacy UI',
    category: categories.noa,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  relationshipShowEnableQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay from a relationship show page',
    category: categories.relationshipShowPageQuickPay,
    ...gaEventArgOverrides,
  }),
  relationshipShowEnableQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the enable QuickPay button on a relationship show page',
    category: categories.relationshipShowPageQuickPay,
    ...gaEventArgOverrides,
  }),
  relationshipShowEnableQuickPayVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the enable QuickPay button on a relationship show page',
    category: categories.relationshipShowPageQuickPay,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  relationshipsIndexEnableQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay from the relationships index page',
    category: categories.relationshipsIndexPageQuickPay,
    ...gaEventArgOverrides,
  }),
  relationshipsIndexEnableQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the enable QuickPay button on the relationships index page',
    category: categories.relationshipsIndexPageQuickPay,
    ...gaEventArgOverrides,
  }),
  relationshipsIndexEnableQuickPayVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the enable QuickPay button on the relationships index page',
    category: categories.relationshipsIndexPageQuickPay,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  selectCarrierLandingPageComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled Select Carrier from select carrier landing page',
    category: categories.payeeSelectCarrierLandingPage,
    ...gaEventArgOverrides,
  }),
  selectCarrierLandingPagePrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the \'Join Now\' Select Carrier button from landing page',
    category: categories.payeeSelectCarrierLandingPage,
    ...gaEventArgOverrides,
  }),
  selectCarrierLandingPageVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the \'Join Now\' Select Carrier button from landing page',
    category: categories.payeeSelectCarrierLandingPage,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  selectCarrierNavbarLinkClicked: (gaEventArgOverrides = {}) => ({
    action: 'Payee clicked the Select Carrier link on navbar',
    category: categories.payeeSelectCarrierNavbarLink,
    ...gaEventArgOverrides,
  }),
  selectCarrierNavbarLinkVisible: (gaEventArgOverrides = {}) => ({
    action: 'Payee saw the Select Carrier link on navbar',
    category: categories.payeeSelectCarrierNavbarLink,
    ...gaEventArgOverrides,
  }),
  submitPaperworkQuickPayComplete: (gaEventArgOverrides = {}) => ({
    action: 'Payee enabled QuickPay from the paperwork success page',
    category: categories.submitPaperworkSuccessQuickPayDialog,
    ...gaEventArgOverrides,
  }),
  submitPaperworkQuickPayPrompt: (gaEventArgOverrides = {}) => ({
    action: 'Payee was prompted to enable QuickPay from the paperwork success page',
    category: categories.submitPaperworkSuccessQuickPayDialog,
    nonInteraction: true,
    ...gaEventArgOverrides,
  }),
  submitPaperworkSupportLinkClicked: (gaEventArgOverrides = {}) => ({
    action: 'User clicked the support link on the Create/Edit Paperwork page',
    category: categories.miscellaneous,
    ...gaEventArgOverrides,
  }),
  submitPaperworkSupportPhoneLinkClicked: (gaEventArgOverrides = {}) => ({
    action: 'User opened the support phone dialog on the Create/Edit Paperwork page',
    category: categories.miscellaneous,
    ...gaEventArgOverrides,
  }),
};

export default events;
